// AdminDashboard.jsx
import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { adminApi } from "./adminApi";
import CreateUserModal from "./CreateUserModal";
import EditUserDrawer from "./EditUserDrawer";
import AdminProfileDrawer from "./AdminProfileDrawer";
import "./AdminDashboard.css";

const PAGE_SIZE = 10;

function daysLeftLabel(expiresAt) {
  const now = new Date();
  const expiry = new Date(expiresAt);
  const diffMs = expiry - now;
  const diffDays = diffMs / (1000 * 60 * 60 * 24);

  if (diffMs < 0) {
    const pastDays = Math.abs(Math.floor(diffDays));
    return { label: `Expired ${pastDays}d ago`, tone: "expired" };
  }
  if (diffDays < 2) {
    const hours = Math.floor(diffMs / (1000 * 60 * 60));
    return { label: `${hours}h left`, tone: "critical" };
  }
  if (diffDays < 7) {
    return { label: `${Math.floor(diffDays)}d left`, tone: "warning" };
  }
  return { label: `${Math.floor(diffDays)}d left`, tone: "healthy" };
}

function formatDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

function formatDateTime(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

// Compares two ISO date strings for sorting. Nulls (e.g. "Never" logged in)
// always sort to the bottom regardless of direction - putting them at the
// top would be confusing either way, so this keeps behavior predictable.
function compareDates(a, b, direction) {
  if (a == null && b == null) return 0;
  if (a == null) return 1;
  if (b == null) return -1;
  const diff = new Date(a).getTime() - new Date(b).getTime();
  return direction === "asc" ? diff : -diff;
}

function EditIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4Z" />
    </svg>
  );
}

function ProfileIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="8" r="4" />
      <path d="M4 21c0-4 4-6 8-6s8 2 8 6" />
    </svg>
  );
}

function SearchIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="11" cy="11" r="8" />
      <path d="m21 21-4.35-4.35" />
    </svg>
  );
}

function SortArrow({ direction }) {
  if (!direction) return <span className="cad-sort-arrow cad-sort-arrow-inactive">↕</span>;
  return <span className="cad-sort-arrow">{direction === "asc" ? "↑" : "↓"}</span>;
}

const STATUS_FILTERS = [
  { value: "all", label: "All" },
  { value: "active", label: "Active" },
  { value: "inactive", label: "Deactivated" },
];

// Maps a sortable column's label to the actual field on the user object.
const SORTABLE_COLUMNS = {
  created: "createdAt",
  expiryDate: "expiresAt",
  timeLeft: "expiresAt", // same underlying field - sorting by soonest/furthest expiry
  lastActive: "lastLoginAt",
};

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [showProfileDrawer, setShowProfileDrawer] = useState(false);

  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);

  const [sortColumn, setSortColumn] = useState(null); // one of SORTABLE_COLUMNS keys, or null
  const [sortDirection, setSortDirection] = useState("asc");

  const loadUsers = useCallback(async () => {
    setError("");
    try {
      const data = await adminApi.listUsers();
      setUsers(data);
    } catch (err) {
      setError(err.message || "Could not load users.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  function handleLogoutFromProfile() {
    navigate("/admin/login");
  }

  function handleSearchChange(e) {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  }

  function handleStatusFilterChange(value) {
    setStatusFilter(value);
    setCurrentPage(1);
  }

  function handleSortClick(columnKey) {
    if (sortColumn !== columnKey) {
      setSortColumn(columnKey);
      setSortDirection("asc");
    } else if (sortDirection === "asc") {
      setSortDirection("desc");
    } else {
      // third click on the same column - reset back to unsorted (default order)
      setSortColumn(null);
      setSortDirection("asc");
    }
    setCurrentPage(1);
  }

  function clearSort() {
    setSortColumn(null);
    setSortDirection("asc");
    setCurrentPage(1);
  }

  const filteredUsers = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    return users.filter((u) => {
      const matchesSearch =
        !term ||
        u.companyName?.toLowerCase().includes(term) ||
        u.username?.toLowerCase().includes(term);

      const matchesStatus =
        statusFilter === "all" ||
        (statusFilter === "active" && u.active) ||
        (statusFilter === "inactive" && !u.active);

      return matchesSearch && matchesStatus;
    });
  }, [users, searchTerm, statusFilter]);

  const sortedUsers = useMemo(() => {
    if (!sortColumn) return filteredUsers;
    const field = SORTABLE_COLUMNS[sortColumn];
    return [...filteredUsers].sort((a, b) => compareDates(a[field], b[field], sortDirection));
  }, [filteredUsers, sortColumn, sortDirection]);

  const totalPages = Math.max(1, Math.ceil(sortedUsers.length / PAGE_SIZE));
  const safePage = Math.min(currentPage, totalPages);
  const pageStart = (safePage - 1) * PAGE_SIZE;
  const paginatedUsers = sortedUsers.slice(pageStart, pageStart + PAGE_SIZE);

  function goToPage(page) {
    setCurrentPage(Math.min(Math.max(1, page), totalPages));
  }

  const isFiltered = searchTerm || statusFilter !== "all";

  return (
    <div className="cad-page">
      <header className="cad-header">
        <div>
          <h1 className="cad-title">Client Access</h1>
          <p className="cad-subtitle">Manage who can view the Eilan portfolio, and for how long.</p>
        </div>
        <div className="cad-header-actions">
          <button className="cad-btn-primary" onClick={() => setShowCreateModal(true)}>
            + Create Access
          </button>
          <button className="cad-btn-ghost" onClick={() => setShowProfileDrawer(true)}>
            <ProfileIcon /> Profile
          </button>
        </div>
      </header>

      {error && <div className="cad-error">{error}</div>}

      <div className="cad-search-row">
        <div className="cad-search-field">
          <span className="cad-search-icon"><SearchIcon /></span>
          <input
            className="cad-search-input"
            type="text"
            placeholder="Search by company or username…"
            value={searchTerm}
            onChange={handleSearchChange}
          />
        </div>

        <div className="cad-status-filter">
          {STATUS_FILTERS.map((f) => (
            <button
              key={f.value}
              className={`cad-status-filter-btn ${statusFilter === f.value ? "cad-status-filter-btn-active" : ""}`}
              onClick={() => handleStatusFilterChange(f.value)}
            >
              {f.label}
            </button>
          ))}
        </div>

        {sortColumn && (
          <button className="cad-clear-sort-btn" onClick={clearSort}>
            Clear sort ×
          </button>
        )}

        {isFiltered && (
          <span className="cad-search-count">
            {sortedUsers.length} result{sortedUsers.length === 1 ? "" : "s"}
          </span>
        )}
      </div>

      <div className="cad-table-wrap">
        {loading ? (
          <div className="cad-empty">Loading…</div>
        ) : sortedUsers.length === 0 ? (
          <div className="cad-empty">
            {isFiltered
              ? "No clients match your filters."
              : 'No client access created yet. Click "Create Access" to generate your first login.'}
          </div>
        ) : (
          <table className="cad-table">
            <thead>
              <tr>
                <th>Company</th>
                <th>Username</th>
                <th className="cad-th-sortable" onClick={() => handleSortClick("created")}>
                  Created <SortArrow direction={sortColumn === "created" ? sortDirection : null} />
                </th>
                <th className="cad-th-sortable" onClick={() => handleSortClick("expiryDate")}>
                  Expiry Date <SortArrow direction={sortColumn === "expiryDate" ? sortDirection : null} />
                </th>
                <th className="cad-th-sortable" onClick={() => handleSortClick("timeLeft")}>
                  Time Left <SortArrow direction={sortColumn === "timeLeft" ? sortDirection : null} />
                </th>
                <th className="cad-th-sortable" onClick={() => handleSortClick("lastActive")}>
                  Last Active <SortArrow direction={sortColumn === "lastActive" ? sortDirection : null} />
                </th>
                <th>Status</th>
                <th className="cad-col-actions">Edit</th>
              </tr>
            </thead>
            <tbody>
              {paginatedUsers.map((user) => {
                const expiry = daysLeftLabel(user.expiresAt);
                return (
                  <tr key={user.id} className="cad-row-clickable" onClick={() => navigate(`/admin/users/${user.id}`)}>
                    <td>{user.companyName}</td>
                    <td className="cad-mono">{user.username}</td>
                    <td>{formatDate(user.createdAt)}</td>
                    <td className="cad-expiry-date">{formatDateTime(user.expiresAt)}</td>
                    <td>
                      <span className={`cad-badge cad-badge-${expiry.tone}`}>{expiry.label}</span>
                    </td>
                    <td>{user.lastLoginAt ? formatDate(user.lastLoginAt) : "Never"}</td>
                    <td>
                      <span className={`cad-status ${user.active ? "cad-status-active" : "cad-status-inactive"}`}>
                        {user.active ? "Active" : "Deactivated"}
                      </span>
                    </td>
                    <td className="cad-col-actions" onClick={(e) => e.stopPropagation()}>
                      <button
                        className="cad-edit-icon-btn"
                        onClick={() => setEditingUser(user)}
                        title="Edit username, password, status, or expiry"
                        aria-label="Edit"
                      >
                        <EditIcon />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {!loading && sortedUsers.length > 0 && totalPages > 1 && (
        <div className="cad-pagination">
          <span className="cad-pagination-info">
            Showing {pageStart + 1}–{Math.min(pageStart + PAGE_SIZE, sortedUsers.length)} of{" "}
            {sortedUsers.length}
          </span>
          <div className="cad-pagination-controls">
            <button
              className="cad-pagination-btn"
              onClick={() => goToPage(safePage - 1)}
              disabled={safePage === 1}
            >
              Previous
            </button>
            {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
              <button
                key={page}
                className={`cad-pagination-btn ${page === safePage ? "cad-pagination-btn-active" : ""}`}
                onClick={() => goToPage(page)}
              >
                {page}
              </button>
            ))}
            <button
              className="cad-pagination-btn"
              onClick={() => goToPage(safePage + 1)}
              disabled={safePage === totalPages}
            >
              Next
            </button>
          </div>
        </div>
      )}

      {showCreateModal && (
        <CreateUserModal
          onClose={() => setShowCreateModal(false)}
          onCreated={loadUsers}
        />
      )}

      {editingUser && (
        <EditUserDrawer
          user={editingUser}
          onClose={() => setEditingUser(null)}
          onUpdated={loadUsers}
        />
      )}

      {showProfileDrawer && (
        <AdminProfileDrawer
          onClose={() => setShowProfileDrawer(false)}
          onLogout={handleLogoutFromProfile}
        />
      )}
    </div>
  );
}